import { SphereButton } from "@components/common/sphere-button";
import { FaroClickableIcon } from "@components/common/faro-clickable-icon";
import { FaroTextButton } from "@components/common/faro-text-button";
import { ImageRenderer } from "@components/common/image-renderer";
import { SphereDivider } from "@components/common/sphere-divider";
import { SlideProgressIndicator } from "@components/splash-screen/slide-progress-indicator";
import {
  SplashScreenContent,
  SplashScreenProps,
} from "@components/splash-screen/splash-screen-utils";
import { useMediaQueryList } from "@hooks/use-media-query";
// eslint-disable-next-line no-restricted-imports
import { Dialog, Grid2, Typography } from "@mui/material";
import { useAppDispatch } from "@store/store-helper";
import { setSplashScreenStatus } from "@store/user/user-slice";
import { setFueFlags } from "@store/user/user-slice-thunk";
import { neutral, CloseIcon, NO_TRANSLATE_CLASS } from "@flat-ui";
import { TutorialEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useMemo, useState } from "react";

interface Props {
  /** The splash screen that needs to be shown to user */
  splashScreen: SplashScreenProps;

  /** Callback to run when splash screen is closed either by finishing it or skipping it */
  onSplashScreenClosed: () => void;
}

// TODO: The value is the same as SPACE_OUTSIDE_OF_MODAL. Refactoring FaroDialog to cover splash-screen makes sense.
/** Defines the side padding for the dialog */
const DIALOG_PADDING = "32px";

/** Shows a splash screen of the given topic */
export function SplashScreen({
  splashScreen,
  onSplashScreenClosed,
}: Props): JSX.Element {
  const [activeScreenNumber, setActiveScreenNumber] = useState<number>(0);
  const { isScreenMdOrLarger } = useMediaQueryList();
  const { trackEvent } = useTrackEvent();

  const dispatch = useAppDispatch();

  /** The content that is currently shown to user */
  const activeScreenContent: SplashScreenContent = useMemo(
    () => splashScreen.content[activeScreenNumber],
    [activeScreenNumber, splashScreen]
  );

  /** Click handler for clicking on a slide indicator */
  function onSlideIndicatorClicked(selectedScreenNumber: number): void {
    trackEvent({
      name: TutorialEvents.jumpToPage,
      props: { topic: splashScreen.topic },
    });
    setActiveScreenNumber(selectedScreenNumber);
  }

  /** Shows the X button to close the dialog. Can be placed on different places depending on the screen size */
  const closeButton = (
    <FaroClickableIcon
      component={CloseIcon}
      onClick={onSkipButtonClicked}
      iconSize="20px"
    />
  );

  function onNextButtonClicked(): void {
    // If the user is on the last screen, then reset the active screen number
    if (activeScreenNumber === splashScreen.content.length - 1) {
      trackEvent({
        name: TutorialEvents.finishTutorial,
        props: { topic: splashScreen.topic },
      });

      setActiveScreenNumber(0);
      dispatch(
        setSplashScreenStatus({
          topic: splashScreen.topic,
          status: "completed",
        })
      );

      void dispatch(setFueFlags());
      onSplashScreenClosed();
    } else {
      trackEvent({
        name: TutorialEvents.selectNextPage,
        props: { topic: splashScreen.topic },
      });
      setActiveScreenNumber((prevValue) => prevValue + 1);
    }
  }

  function onSkipButtonClicked(): void {
    trackEvent({
      name: TutorialEvents.skipTutorial,
      props: { topic: splashScreen.topic },
    });

    dispatch(
      setSplashScreenStatus({
        topic: splashScreen.topic,
        status: "skipped",
      })
    );

    void dispatch(setFueFlags());
    onSplashScreenClosed();
  }

  return (
    <Dialog open={true} fullWidth={true} maxWidth="lg">
      <Grid2 container alignItems={{ xs: "center", md: "normal" }}>
        <Grid2
          sx={{
            backgroundColor: neutral[100],
            padding: DIALOG_PADDING,
          }}
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <Grid2 container>
            {!isScreenMdOrLarger && (
              <Grid2
                sx={{
                  display: "flex",
                  justifyContent: "right",
                }}
                size={12}
              >
                {closeButton}
              </Grid2>
            )}
            <Grid2
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              size={12}
            >
              <ImageRenderer
                image={{
                  src: activeScreenContent.image.src,
                  altText: activeScreenContent.image.altText,
                }}
                sx={{
                  height: {
                    xs: "200px",
                    md: "440px",
                  },
                  width: "100%",
                  objectFit: "contain",
                }}
              />
            </Grid2>
          </Grid2>
        </Grid2>
        <Grid2
          container
          direction="column"
          justifyContent="space-between"
          sx={{
            padding: DIALOG_PADDING,
          }}
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <Grid2 container direction="column">
            <Grid2
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: "24px" }}
            >
              <Typography
                sx={{
                  fontSize: 10,
                  color: neutral[600],
                  fontWeight: "bold",
                }}
              >
                Step {activeScreenNumber + 1} of {splashScreen.content.length}
              </Typography>
              {isScreenMdOrLarger && closeButton}
            </Grid2>
            <Grid2 sx={{ mb: "20px" }}>
              <Typography
                sx={{ fontSize: 20, color: neutral[800] }}
                className={
                  splashScreen.shouldBlockTranslation
                    ? NO_TRANSLATE_CLASS
                    : undefined
                }
              >
                {activeScreenContent.title}
              </Typography>
              <SphereDivider sx={{ mt: "24px", mb: "32px" }} />
              <Typography
                sx={{ fontSize: 16, color: neutral[800] }}
                className={
                  splashScreen.shouldBlockTranslation
                    ? NO_TRANSLATE_CLASS
                    : undefined
                }
              >
                {activeScreenContent.description}
              </Typography>
            </Grid2>
          </Grid2>
          <Grid2
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <FaroTextButton
              onClick={() => {
                trackEvent({
                  name: TutorialEvents.selectPreviousPage,
                  props: { topic: splashScreen.topic },
                });
                setActiveScreenNumber((prevValue) => prevValue - 1);
              }}
              sx={{
                visibility: activeScreenNumber === 0 ? "hidden" : "visible",
                fontWeight: "600",
              }}
            >
              Back
            </FaroTextButton>
            <SlideProgressIndicator
              activeScreenNumber={activeScreenNumber}
              totalNumberOfScreens={splashScreen.content.length}
              onSlideIndicatorClicked={onSlideIndicatorClicked}
            />
            <SphereButton onClick={onNextButtonClicked}>
              {activeScreenNumber === splashScreen.content.length - 1
                ? "Done"
                : "Next"}
            </SphereButton>
          </Grid2>
        </Grid2>
      </Grid2>
    </Dialog>
  );
}
